import { DomainConfig, ProgramDomain } from '../types';

export const DOMAIN_CONFIGS: Record<ProgramDomain, DomainConfig> = {
  learning: {
    id: 'learning',
    icon: '🎓',
    label: 'Learning & Education',
    description: 'Master new skills, languages, or knowledge domains',
    exampleGoals: [
      'Learn JavaScript programming',
      'Master Spanish language',
      'Get AWS certified',
      'Learn data science'
    ],
    // ⚡ AI CHAT: Dynamic questions generated based on user's specific goal
    questions: [],
    systemPromptAddition: `
This is a LEARNING program focused on knowledge acquisition and skill development. Generate a structured learning journey that:

PROGRAM STRUCTURE:
- Create a logical learning progression from fundamentals to advanced concepts
- Include prerequisite knowledge identification and building
- Design progressive difficulty levels with mastery checkpoints
- Incorporate spaced repetition and review cycles

CONTENT REQUIREMENTS:
- Provide specific, actionable learning objectives for each milestone
- Include hands-on practice exercises and real-world applications
- Recommend specific resources (books, courses, websites, tools)
- Create measurable assessment criteria for each milestone
- Include common learning pitfalls and how to overcome them

PEDAGOGICAL APPROACH:
- Balance theory with practical application (70% practice, 30% theory)
- Include project-based learning opportunities
- Design collaborative or peer-learning elements where applicable
- Incorporate self-assessment and reflection exercises
- Create motivation-maintaining elements like progress celebrations

ADAPTATION CONSIDERATIONS:
- Account for different learning styles (visual, auditory, kinesthetic)
- Provide alternative learning paths for different skill levels
- Include accessibility considerations and inclusive learning methods
- Design for sustainable learning pace to prevent burnout
`
  },

  fitness: {
    id: 'fitness',
    icon: '💪',
    label: 'Fitness & Health',
    description: 'Build strength, improve endurance, or enhance overall health',
    exampleGoals: [
      'Build muscle mass',
      'Lose 10kg of fat',
      'Run a marathon',
      'Improve flexibility'
    ],
    // ⚡ AI CHAT: Dynamic questions generated based on user's specific goal
    questions: [],
    systemPromptAddition: `
This is a FITNESS program focused on physical development and health improvement. Generate a comprehensive fitness journey that:

PROGRAM STRUCTURE:
- Implement progressive overload with periodized training cycles
- Design workout frequency based on user goals and recovery capacity
- Include proper warm-up, main workout, and cool-down sequences
- Plan strategic rest days and active recovery sessions
- Create phased progression (preparation → base building → strength/power → peak performance)

SAFETY & TECHNIQUE:
- Emphasize proper form and technique for all exercises
- Include injury prevention strategies and risk assessment
- Design modifications for different fitness levels and limitations
- Incorporate mobility work and flexibility training
- Include contraindications and when to seek professional advice

TRACKING & MEASUREMENT:
- Define specific, measurable performance metrics
- Include body measurements, strength assessments, and fitness tests
- Design progress photos and self-assessment protocols
- Create objective success criteria for each milestone
- Include subjective wellness indicators (energy, mood, sleep quality)

NUTRITION INTEGRATION:
- Provide nutrition guidance aligned with specific fitness goals
- Include macro and micronutrient recommendations
- Design meal timing around workout schedules
- Incorporate hydration and supplementation guidance
- Account for dietary restrictions and preferences

RECOVERY & SUSTAINABILITY:
- Plan deload weeks and recovery phases for long-term programs
- Include stress management and sleep optimization strategies
- Design for long-term adherence and habit formation
- Incorporate variety to prevent mental and physical burnout
- Include cross-training and complementary activities
`
  },

  creative: {
    id: 'creative',
    icon: '🎨',
    label: 'Creative Skills',
    description: 'Develop artistic abilities in music, art, writing, or design',
    exampleGoals: [
      'Learn to play guitar',
      'Improve drawing skills',
      'Write a novel',
      'Master photography'
    ],
    // ⚡ AI CHAT: Dynamic questions generated based on user's specific goal
    questions: [],
    systemPromptAddition: `
This is a CREATIVE SKILLS program focused on artistic development and self-expression. Generate an inspiring creative journey that:

FOUNDATIONAL SKILLS:
- Establish fundamental techniques and principles for the chosen art form
- Build technical proficiency through structured exercises
- Develop muscle memory and instinctive execution
- Create understanding of tools, materials, and their applications
- Include art theory and historical context where relevant

PRACTICE METHODOLOGY:
- Design daily practice routines with progressive complexity
- Include warm-up exercises and technical drills
- Incorporate variety to prevent creative stagnation
- Balance structured learning with free exploration
- Include reflection and self-assessment practices

CREATIVE DEVELOPMENT:
- Spark imagination through creative prompts and exercises
- Encourage experimentation and risk-taking in creative work
- Study and analyze exemplary works for inspiration and learning
- Develop personal style and artistic voice
- Include collaborative and feedback-oriented activities

PROJECT DEVELOPMENT:
- Create milestone-based projects of increasing scope and complexity
- Include planning, execution, and revision phases
- Design portfolio-building opportunities
- Incorporate presentation and sharing of creative work
- Build toward capstone projects that showcase growth

MENTAL & EMOTIONAL SUPPORT:
- Address creative blocks and artist's doubt
- Include motivation-maintaining strategies
- Develop constructive self-critique skills
- Build resilience and persistence
- Foster creative confidence and self-belief

TECHNICAL PROGRESSION:
- Advance from basic techniques to advanced mastery
- Include cross-disciplinary skill integration
- Develop professional practices and habits
- Prepare for real-world creative challenges
- Build sustainable creative practice for long-term growth
`
  },

  habits: {
    id: 'habits',
    icon: '🧘',
    label: 'Habits & Lifestyle',
    description: 'Build positive routines, improve productivity, or enhance wellbeing',
    exampleGoals: [
      'Develop a meditation practice',
      'Wake up at 5 AM consistently',
      'Build a morning routine',
      'Reduce screen time'
    ],
    // ⚡ AI CHAT: Dynamic questions generated based on user's specific goal
    questions: [],
    systemPromptAddition: `
This is a HABIT FORMATION program focused on building sustainable lifestyle changes and positive routines. Generate a comprehensive habit development journey that:

HABIT DESIGN PRINCIPLES:
- Start with tiny micro-habits (2-minute versions) and gradually scale up
- Design trigger-behavior-reward loops for automatic execution
- Implement habit stacking by linking new habits to existing routines
- Create implementation intentions ("When X happens, then I will Y")
- Make habits obvious, attractive, easy, and satisfying (BJ Fogg's principles)

PROGRESSION STRATEGY:
- Use gradual intensity scaling (start at 20% effort, build to 100%)
- Include milestone celebrations and reward systems
- Design streak-maintenance strategies and recovery protocols
- Create accountability mechanisms and social support systems
- Build habit identity ("I am the type of person who...")

ENVIRONMENTAL DESIGN:
- Remove friction and create enabling environments
- Design cue-rich contexts that trigger desired behaviors
- Include environmental restructuring and habit reminders
- Plan for common obstacles and relapse prevention
- Create supportive systems and accountability structures

TRACKING & MOTIVATION:
- Implement visual progress tracking and streak visualization
- Include reflection and adjustment periods
- Design celebration rituals for consistency milestones
- Create motivation-maintaining strategies for long-term adherence
- Include flexibility for life changes and temporary setbacks

PSYCHOLOGICAL FRAMEWORK:
- Address limiting beliefs and identity conflicts
- Build self-efficacy through progressive mastery experiences
- Include cognitive behavioral techniques for habit change
- Design for long-term sustainability and automaticity
- Incorporate mindfulness and self-awareness practices

RECOVERY & RESILIENCE:
- Plan for setbacks, relapses, and recovery strategies
- Include forgiveness and restart protocols
- Design habit maintenance phases after initial formation
- Create contingency plans for high-risk situations
- Build psychological resilience and coping mechanisms
`
  },

  custom: {
    id: 'custom',
    icon: '🎯',
    label: 'Custom Goal',
    description: 'Any other goal that doesn\'t fit the above categories',
    exampleGoals: [
      'Plan a wedding',
      'Start a side business',
      'Organize my home',
      'Plan a world trip'
    ],
    // ⚡ CUSTOM DOMAIN: Uses AI chat for personalization instead of static questions
    // The AI will dynamically generate 2-3 relevant questions based on the goal
    questions: [],
    systemPromptAddition: `
This is a CUSTOM program. Focus on:
- Understanding the unique nature of this goal
- Breaking down complex objectives into manageable tasks
- Identifying key milestones and dependencies
- Providing flexibility for unexpected challenges
- Clear success criteria and tracking methods
- Resource recommendations tailored to context
- Risk management and contingency planning
- Regular check-ins and adjustment points
- Celebrating progress and maintaining motivation
`
  }
};

// Helper function to get domain config
export const getDomainConfig = (domain: ProgramDomain): DomainConfig => {
  return DOMAIN_CONFIGS[domain];
};

// Helper function to get all domains
export const getAllDomains = (): DomainConfig[] => {
  return Object.values(DOMAIN_CONFIGS);
};

